export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

const JSON_INDENTATION = 4;
const MAX_INLINE_ARRAY_LENGTH = 120;

export const formatJson = (value: JsonValue, depth = 0): string => {
    if (value === null || typeof value !== "object") return JSON.stringify(value);

    const indentation = " ".repeat(depth * JSON_INDENTATION);
    const childIndentation = " ".repeat((depth + 1) * JSON_INDENTATION);

    if (Array.isArray(value)) {
        if (!value.length) return "[]";

        const onlyPrimitiveValues = value.every(item => item === null || typeof item !== "object");

        if (onlyPrimitiveValues) {
            const inlineValue = `[${value.map(item => JSON.stringify(item)).join(", ")}]`;

            if (indentation.length + inlineValue.length <= MAX_INLINE_ARRAY_LENGTH) {
                return inlineValue;
            }
        }

        return `[` +
            `\n${value.map(item => `${childIndentation}${formatJson(item, depth + 1)}`).join(",\n")}` +
            `\n${indentation}]`;
    }

    const entries = Object.entries(value);

    if (!entries.length) return "{}";

    return `{` +
        `\n${entries.map(([key, item]) => `${childIndentation}${JSON.stringify(key)}: ${formatJson(item, depth + 1)}`).join(",\n")}` +
        `\n${indentation}}`;
};

const parseJsonValue = (input: string): unknown => {
    try {
        return JSON.parse(input);
    } catch (error) {
        const trimmedInput = input.trim();
        const unwrappedInput = trimmedInput.slice(1, -1).trim();

        // Some copied values wrap raw JSON in quotes without escaping its contents.
        if (
            trimmedInput.startsWith('"') && trimmedInput.endsWith('"') &&
            ((unwrappedInput.startsWith("[") && unwrappedInput.endsWith("]")) ||
                (unwrappedInput.startsWith("{") && unwrappedInput.endsWith("}")))
        ) {
            try {
                return parseJsonValue(unwrappedInput);
            } catch {
                // Keep the existing normalization and error handling for invalid JSON.
            }
        }

        const normalizedInput = input.replace(/[“”]/g, '"').replace(
            /"(?:\\.|[^"\\])*"|'((?:\\.|[^'\\])*)'/g,
            (token: string, singleQuotedValue: string | undefined) => {
                if (singleQuotedValue === undefined) return token;

                // Convert string delimiters without changing apostrophes inside JSON strings.
                const jsonString = singleQuotedValue.replace(/\\.|"/g, character => {
                    if (character === "\\'") return "'";
                    if (character === '"') return '\\"';
                    return character;
                });
                return `"${jsonString}"`;
            },
        ).replace(
            /"(?:\\.|[^"\\])*"|\s/g,
            token => token.startsWith('"') ? token : " ",
        );
        if (normalizedInput === input) throw error;

        return JSON.parse(normalizedInput);
    }
};

export const parseInput = (input: string): JsonValue => {
    let value: unknown = parseJsonValue(input);

    // Also accept values such as "{\"name\":\"Ocean Planner\"}".
    for (let depth = 0; depth < 3 && typeof value === "string"; depth += 1) {
        const trimmedValue = value.trim();

        if (!trimmedValue.startsWith("{") && !trimmedValue.startsWith("[")) break;

        value = parseJsonValue(trimmedValue);
    }

    return value as JsonValue;
};

